import { useCallback, useEffect, useState } from "react";
import { MdAdd, MdDelete, MdError, MdSave } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
//
import {
    useCreateTierDataMutation,
    useDeleteTierDataMutation,
    useFetchTierDataMutation,
    useUpdateTierDataMutation,
} from "../../../features/tenant/schemeTier/schemeTierApi";
import { Switch } from "../../../components/Switch";
import { validateData } from "../../../utils/validateData";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import ModalComponent from "../../../components/Tenant/ModalComponent/ModalComponent";

const defaultSchema = {
    name: {
        required: true,
        type: "string",
        trim: true,
    },
    range_start: {
        required: true,
        type: "number",
        coerce: true,
        min: -1,
        exclusiveMin: true,
    },
    range_end: {
        required: false,
        type: "number",
        // force type conversion
        coerce: true,
        min: 0,
        exclusiveMin: true,
        greaterThanField: "range_start",
    },
    stars: {
        required: true,
        type: "string",
        coerce: true,
    },
    status: {
        required: false,
        type: "boolean",
        coerce: true,
        default: false,
    },
};

const Input = ({ label = "", error = false, ...other }) => {
    return (
        <div className="w-full flex flex-col justify-start items-start">
            {label && <label className="mb-0 pl-1 text-sm">{label}</label>}

            <input
                type="text"
                className={`input min-w-48 w-full ${
                    error ? "!border-red-500" : ""
                }`}
                placeholder="Tier name..."
                {...other}
            />

            <div className="error-message min-h-6">
                {error?.message && typeof error?.message === "string" && (
                    <span className="text-red-500 text-sm">
                        {error?.message}
                    </span>
                )}
            </div>
        </div>
    );
};

const ManageSchemeTier = () => {
    const navigate = useNavigate();
    const location = useLocation();

    let token = sessionStorage.getItem("access_token");
    let tenant_id = JSON.parse(sessionStorage.getItem("vendorData"))?.tenant_id;

    const [globalError, setGlobalError] = useState(null);

    const [fetchTierData, { isLoading, error, data: tierData }] =
        useFetchTierDataMutation();

    const pathname = location?.pathname;
    const schemeData = location?.state;

    const initialize = useCallback(async () => {
        fetchTierData({
            token: token,
            tenant_id: tenant_id,
            scheme_id: schemeData?.id,
        });
    }, [fetchTierData, token, tenant_id, schemeData]);

    useEffect(() => {
        if (!schemeData) {
            navigate(pathname?.split("/tier")[0]);
        } else {
            initialize();
        }
    }, []);

    return (
        <div className="w-full bg-gray-200 h-full justify-center">
            <div className="w-full">
                <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
                    Manager Scheme Tier
                    <LinksComponent />
                </div>
            </div>

            <div className="w-full px-2 py-2 flex justify-center items-start">
                <div className="max-w-screen w-full">
                    {!!globalError && (
                        <div className="mb-2 bg-red-500 text-black w-full rounded-md px-4 py-3 flex flex-row items-center justify-start gap-2">
                            <MdError className="fill-white" />

                            <span className="text-sm text-white">
                                {globalError}
                            </span>
                        </div>
                    )}

                    <div className="px-4 py-4 bg-white flex flex-col gap-3 w-full !border rounded-sm">
                        <h4 className="text-xl font-semibold">
                            Create new scheme tier
                        </h4>

                        <div className="w-full overflow-x-auto">
                            <div className="w-full flex flex-row gap-2">
                                <RenderRow
                                    isNew={true}
                                    token={token}
                                    initialize={initialize}
                                    schemeData={schemeData}
                                    setGlobalError={setGlobalError}
                                    tenant_id={tenant_id}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="mt-4 px-4 py-4 bg-white flex flex-col gap-3 w-full !border rounded-sm">
                        <h4 className="text-xl font-semibold">
                            Existing scheme tiers
                        </h4>

                        <div className="w-full overflow-x-auto flex flex-col gap-2">
                            {tierData?.body?.map((item, index) => (
                                <RenderRow
                                    rowData={item}
                                    key={index}
                                    token={token}
                                    initialize={initialize}
                                    schemeData={schemeData}
                                    setGlobalError={setGlobalError}
                                    tenant_id={tenant_id}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const RenderRow = ({
    isNew = false,
    token,
    tenant_id,
    initialize,
    schemeData,
    setGlobalError,
    rowData = {},
}) => {
    const primaryColor = JSON.parse(sessionStorage.getItem("vendorTheme"))
        ?.theme?.color_shades;

    const [errors, setErrors] = useState({});
    const [selectedData, setSelectedData] = useState({
        name: rowData?.name || "",
        range_start: rowData?.range_start || 0,
        range_end: rowData?.range_end || 0,
        stars: rowData?.stars || "",
        status: rowData ? rowData?.status === "1" : false,
    });
    const [confirmationState, setConfirmationState] = useState({
        text: "",
        error: null,
        show: false,
    });
    const [isModified, setIsModified] = useState(false);

    const [createTierData, { isLoading: createLoading, error: createError }] =
        useCreateTierDataMutation();
    const [updateTierData, { isLoading: updateLoading, error: updateError }] =
        useUpdateTierDataMutation();
    const [deleteTierData, { isLoading: deleteLoading, error: deleteError }] =
        useDeleteTierDataMutation();

    const isLoading = createLoading || updateLoading;
    const isError = createError || updateError;

    const handleAction = async (action = "", data = {}) => {
        const { key, value } = data || {};

        setErrors((prev) => {
            let currentMap = { ...prev };

            delete currentMap[key];

            return currentMap;
        });

        setGlobalError(null);

        try {
            switch (action) {
                case "input":
                    setIsModified(true);
                    setSelectedData((prev) => ({
                        ...prev,
                        [key]: value,
                    }));

                    break;

                case "create":
                    const createValidation = await validateData(
                        selectedData,
                        defaultSchema
                    );

                    if (!createValidation?.status) {
                        setErrors(createValidation?.data?.errors || {});

                        break;
                    }

                    const response = await createTierData({
                        token,
                        tenant_id,
                        body: {
                            scheme_id: schemeData?.id,
                            name: createValidation?.data?.name,
                            range_start: createValidation?.data?.range_start,
                            range_end: createValidation?.data?.range_end,
                            stars: createValidation?.data?.stars,
                            status: "1",
                        },
                    }).unwrap();

                    if (response?.data?.success || response?.success) {
                        initialize();
                        setIsModified(false);
                    } else {
                        throw new Error(
                            response?.data?.message ||
                                response?.message ||
                                "Unexpected error"
                        );
                    }

                    break;

                case "update":
                    if (rowData?.id) {
                        const updateValidation = await validateData(
                            selectedData,
                            defaultSchema
                        );

                        if (!updateValidation?.status) {
                            setErrors(updateValidation?.data?.errors || {});

                            break;
                        }

                        const updateResponse = await updateTierData({
                            token,
                            tenant_id,
                            id: rowData?.id,
                            body: {
                                scheme_id: schemeData?.id,
                                name: updateValidation?.data?.name,
                                range_start:
                                    updateValidation?.data?.range_start,
                                range_end: updateValidation?.data?.range_end,
                                stars: updateValidation?.data?.stars,
                                status: updateValidation?.data?.status
                                    ? "1"
                                    : "2",
                            },
                        }).unwrap();

                        if (
                            updateResponse?.data?.success ||
                            updateResponse?.success
                        ) {
                            // initialize();
                            setIsModified(false);
                        } else {
                            throw new Error(
                                response?.data?.message ||
                                    response?.message ||
                                    "Unexpected error"
                            );
                        }
                    }

                    break;

                case "delete":
                    if ("yes" !== confirmationState?.text?.trim()?.toLowerCase()) {
                        setConfirmationState((prev) => ({
                            ...prev,
                            show: true,
                            error: "Confirmation incorrect",
                        }));
                        break;
                    }

                    setConfirmationState((prev) => ({
                        ...prev,
                        show: false,
                        error: false,
                    }));

                    const deleteResponse = await deleteTierData({
                        token,
                        tenant_id,
                        id: rowData?.id,
                        scheme_id: schemeData?.id,
                    }).unwrap();

                    if (
                        deleteResponse?.data?.success ||
                        deleteResponse?.success
                    ) {
                        initialize();
                        setIsModified(false);
                    } else {
                        throw new Error(
                            response?.data?.message ||
                                response?.message ||
                                "Unexpected error"
                        );
                    }

                    break;
            }
        } catch (error) {
            console.log(error);
            setGlobalError(
                error?.data?.message || error?.message || "Unexpected error"
            );
        }
    };

    return (
        <>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    handleAction(isNew ? "create" : "update");
                }}
                className="w-full flex flex-row items-end gap-4"
                style={{
                    ...(!!isLoading && {
                        pointerEvents: "none",
                        opacity: 0.7,
                    }),
                }}
            >
                <Input
                    label="Tier name"
                    placeholder="Tier name..."
                    value={selectedData?.name}
                    error={errors?.name}
                    onChange={(e) =>
                        handleAction("input", {
                            key: "name",
                            value: e?.target?.value,
                        })
                    }
                />

                <Input
                    type="number"
                    label="Range start"
                    placeholder="Range start..."
                    value={selectedData?.range_start}
                    error={errors?.range_start}
                    onChange={(e) =>
                        handleAction("input", {
                            key: "range_start",
                            value: e?.target?.value,
                        })
                    }
                />

                <Input
                    type="number"
                    label="Range end"
                    placeholder="Range end..."
                    value={selectedData?.range_end}
                    error={errors?.range_end}
                    onChange={(e) =>
                        handleAction("input", {
                            key: "range_end",
                            value: e?.target?.value,
                        })
                    }
                />

                <Input
                    type="text"
                    label="Stars"
                    placeholder="Stars..."
                    value={selectedData?.stars}
                    error={errors?.stars}
                    onChange={(e) =>
                        handleAction("input", {
                            key: "stars",
                            value: e?.target?.value,
                        })
                    }
                />

                {!isNew && (
                    <div className="min-w-24 mb-4">
                        <Switch
                            label={selectedData?.status ? "Active" : "Inactive"}
                            checked={selectedData?.status}
                            onChange={() =>
                                handleAction("input", {
                                    key: "status",
                                    value: !selectedData?.status,
                                })
                            }
                        />
                    </div>
                )}

                <button
                    className="uppercase mb-4 rounded-md border-none outline-none px-4 py-2 flex flex-row items-center justify-center gap-1"
                    disabled={isLoading || !isModified}
                    type="submit"
                    style={{
                        background: primaryColor[700],
                        color: primaryColor[50],
                        ...(isLoading ||
                            (!isModified && {
                                filter: "saturate(0)",
                                opacity: 0.5,
                            })),
                    }}
                >
                    {isNew ? (
                        <>
                            <MdAdd /> Create
                        </>
                    ) : (
                        <>
                            <MdSave /> Update
                        </>
                    )}
                </button>

                {!isNew && (
                    <button
                        className="uppercase mb-4 rounded-full border-none outline-none px-4 py-2 flex flex-row items-center justify-center gap-1"
                        type="button"
                        disabled={isLoading}
                        style={{
                            backgroundColor: "red",
                            color: "white",
                            ...(isLoading && {
                                filter: "saturate(0)",
                                opacity: 0.5,
                            }),
                        }}
                        onClick={() =>
                            setConfirmationState((prev) => ({
                                ...prev,
                                show: true,
                            }))
                        }
                    >
                        <MdDelete /> Delete
                    </button>
                )}
            </form>

            {confirmationState.show && (
                <ModalComponent
                    size="md"
                    show={confirmationState.show}
                    setShow={() =>
                        setConfirmationState((prev) => ({
                            ...prev,
                            show: false,
                        }))
                    }
                >
                    <div className="px-4 py-3 flex flex-col gap-2 items-end w-full">
                        {!!confirmationState.error && (
                            <div className="mb-2 bg-red-500 text-black w-full rounded-md px-4 py-3 flex flex-row items-center justify-start gap-2">
                                <MdError className="fill-white" />

                                <span className="text-sm text-white">
                                    {confirmationState.error}
                                </span>
                            </div>
                        )}

                        <Input
                            label={
                                <span className="text-lg">
                                    Enter <b>yes</b> to confirm
                                </span>
                            }
                            className="input w-full"
                            placeholder="Confirmation..."
                            value={confirmationState.text}
                            onChange={(e) => {
                                setConfirmationState((prev) => ({
                                    ...prev,
                                    text: e.target.value?.trim(),
                                }));
                            }}
                        />

                        <button
                            className="uppercase mb-4 rounded-md border-none outline-none px-4 py-2 flex flex-row items-center justify-center gap-1"
                            style={{
                                background: primaryColor[700],
                                color: primaryColor[50],
                            }}
                            onClick={() => handleAction("delete")}
                        >
                            Submit
                        </button>
                    </div>
                </ModalComponent>
            )}
        </>
    );
};

export default ManageSchemeTier;
